import React from 'react';
import { Box, Button, Flex, Grid, Image, Text, Tooltip } from '@chakra-ui/react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { SpecificHeaderInfo, SpecificHeaderInfoVariant } from './specificHeaderInfo';
import { Cartridge } from '../../../components/GamesList/cartridge';
import { useTranslation } from 'react-i18next';
import { LinkIcon } from '../../../utils/icons/link';
import { useGameColorsProvider } from '../../../providers/Theme/gameColorsProvider';
import { ethers } from 'ethers';
import { IGame } from '../../../providers/Games/useGames';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { Link } from 'react-router-dom';
import { isLauncher } from '../../../utils/launcher';
import { formatUsd, trimAddress } from '../../../utils/parser';

interface IMainInfo {
	account: any;
	price: string;
	priceInHash: string;
	name: string;
	initialPrice: any;
	totalSupply: any;
	marketCap: any;
	type: any;
	priceChange: number;
	marketCapChange: number;
	symbol: string;
	image: string;
	game: IGame;
	marketplaceFee: string;
}

export const MainInfo = ({
	name,
	price,
	priceInHash,
	account,
	initialPrice,
	totalSupply,
	marketCap,
	type,
	priceChange,
	marketCapChange,
	symbol,
	image,
	game,
	marketplaceFee,
}: IMainInfo) => {
	const { t } = useTranslation();
	const { textColor } = useGameColorsProvider();

	const handleAddTokenToWallet = async () => {
		await (window as any).ethereum.request({
			method: 'wallet_watchAsset',
			params: {
				type: 'ERC20',
				options: {
					address: account,
					symbol,
					decimals: 2,
					image,
				},
			},
		});
	};

	return (
		<>
			<Flex
				direction="column"
				display={{ base: 'flex', hashup: 'none' }}
				gridGap="16px"
				zIndex="11"
			>
				<Flex
					gridGap="8px"
					alignItems="center"
				>
					<Text
						overflow="hidden"
						overflowWrap="anywhere"
						fontSize={{ base: '18px', hashup: '28px' }}
						lineHeight={{ base: '22px', hashup: '33px' }}
						fontWeight="600"
					>
						{name}
					</Text>
					<Cartridge
						symbol={symbol}
						type={type}
						padding="3px 5px"
						color="white"
					/>
				</Flex>
				<Flex justifyContent="space-between">
					<Flex gridGap="8px">
						<Text
							fontSize={{ base: '18px', hashup: '28px' }}
							lineHeight={{ base: '23px', hashup: '35px' }}
							fontWeight="600"
						>
							{formatUsd(price)}
						</Text>
						{game.address === '0xd037102c5bc71e576fe9c0b1b8398e58be1e4ef7' && (
							<Text
								fontSize={{ base: '10px', hashup: '20px' }}
								lineHeight={{ base: '13px', hashup: '25px' }}
								fontWeight="600"
								color="action"
								textDecoration="line-through"
							>
								100$
							</Text>
						)}
					</Flex>
					<Flex
						background="white"
						borderRadius="6px"
						alignItems="center"
						padding="2px 8px"
						gridGap="8px"
					>
						<Button
							onClick={handleAddTokenToWallet}
							outline="none"
							variant="unstyled"
							minWidth="auto"
							height="auto"
							display="flex"
							gridGap="4px"
							alignItems="center"
							lineHeight="17px"
						>
							<Image
								src="/icons/metamask_small.svg"
								alt="MetaMask"
								h="14px"
							/>
							<Text
								textTransform="uppercase"
								fontSize="9px"
								color="#00000080"
							>
								Add to wallet
							</Text>
						</Button>
					</Flex>
				</Flex>
				<Flex gridGap="18px">
					<SpecificHeaderInfo
						variant={SpecificHeaderInfoVariant.SMALL}
						text={t('gameOverview.totalSupply').toString()}
						body={<Text>{totalSupply}</Text>}
					/>
					<SpecificHeaderInfo
						variant={SpecificHeaderInfoVariant.SMALL}
						text={t('gameOverview.marketCap')}
						body={<Text>Soon</Text>}
					/>
					<SpecificHeaderInfo
						text={t('gameOverview.cartridge')}
						body={
							<Cartridge
								padding="3px 5px"
								type={type.toUpperCase()}
								color="white"
							/>
						}
					/>
					<SpecificHeaderInfo
						variant={SpecificHeaderInfoVariant.SMALL}
						text={'Creator Fee'}
						body={<Text>{Number(ethers.utils.formatUnits(game.creatorFee || '0', 0))}%</Text>}
					/>
					<SpecificHeaderInfo
						variant={SpecificHeaderInfoVariant.SMALL}
						text={'Marketplace Fee'}
						body={<Text>{Number(ethers.utils.formatUnits(game.marketplaceFee || '0', 0))}%</Text>}
					/>
				</Flex>
			</Flex>
			<Grid
				display={{ base: 'none', hashup: 'grid' }}
				gridTemplateAreas="'name name' 'price price' 'media .'"
				gridGap="16px"
				gridColumnGap="32px"
				paddingX={{ base: '12px', hashup: '0' }}
				position="relative"
			>
				<Flex
					flexDirection="column"
					gridGap="6px"
					gridArea="name"
				>
					<Flex
						width="100%"
						justify="space-between"
					>
						<Flex
							gridGap="16px"
							alignItems="center"
						>
							<Tooltip
								placement="bottom-end"
								label={name}
							>
								<Text
									overflow="hidden"
									maxWidth="calc(854px - 2em)"
									overflowWrap="anywhere"
									fontSize={{ base: '18px', hashup: '28px' }}
									lineHeight={{ base: '22px', hashup: '33px' }}
									fontWeight="600"
								>
									{name}
								</Text>
							</Tooltip>
							<Cartridge
								symbol={symbol}
								type={type}
								padding="3px 5px"
								color="white"
							/>
						</Flex>
						{isLauncher() && (
							<Flex
								align="center"
								lineHeight="18px"
							>
								<ChevronLeftIcon />
								<Link to="/">
									<Text fontSize="14px">Go back</Text>
								</Link>
							</Flex>
						)}
						{/* <Image src="/icons/favorite.svg" alt="Mark as favorite" cursor="pointer" /> */}
					</Flex>
					<Flex
						fontSize="14px"
						lineHeight="17px"
						color={`${textColor}80`}
						display={{ base: 'none', hashup: 'flex' }}
						gridGap="20px"
						alignItems="center"
					>
						<Flex
							alignItems="center"
							gridGap="5px"
						>
							{trimAddress(account)}

							<Flex
								maxW="14px"
								maxH="14px"
							>
								<CopyToClipboard text={account}>
									<LinkIcon
										color={`${textColor}80`}
										cursor="pointer"
										height="auto"
										width="1em"
									/>
								</CopyToClipboard>
							</Flex>
						</Flex>
					</Flex>
				</Flex>
				<Flex
					gridGap="48px"
					gridArea="price"
				>
					<Flex
						flexDirection="column"
						gridGap="6px"
					>
						<Flex
							gridGap="43px"
							alignItems="flex-start"
						>
							<Flex
								flexDirection="column"
								align="flex-start"
								gridGap="15px"
							>
								<Flex
									gridGap="8px"
									width="180px"
								>
									<Text
										fontSize={{ base: '18px', hashup: '28px' }}
										lineHeight={{ base: '23px', hashup: '35px' }}
										fontWeight="600"
									>
										{formatUsd(price)}
									</Text>
									{game.address === '0xd037102c5bc71e576fe9c0b1b8398e58be1e4ef7' && (
										<Text
											fontSize={{ base: '10px', hashup: '20px' }}
											lineHeight={{ base: '13px', hashup: '25px' }}
											fontWeight="600"
											color="action"
											textDecoration="line-through"
										>
											100$
										</Text>
									)}
								</Flex>
								<Flex
									background="white"
									borderRadius="6px"
									alignItems="center"
									padding="2px 8px"
									gridGap="8px"
								>
									<Button
										onClick={handleAddTokenToWallet}
										outline="none"
										variant="unstyled"
										minWidth="auto"
										height="auto"
										display="flex"
										gridGap="4px"
										alignItems="center"
										lineHeight="17px"
									>
										<Image
											src="/icons/metamask_small.svg"
											alt="MetaMask"
											h="14px"
										/>
										<Text
											textTransform="uppercase"
											fontSize="9px"
											color="#00000080"
										>
											Add to wallet
										</Text>
									</Button>
								</Flex>
							</Flex>

							<SpecificHeaderInfo
								variant={SpecificHeaderInfoVariant.SMALL}
								text={t('gameOverview.totalSupply').toString()}
								body={<Text>{totalSupply}</Text>}
							/>
							<SpecificHeaderInfo
								variant={SpecificHeaderInfoVariant.SMALL}
								text={t('gameOverview.marketCap')}
								body={
									<>
										<Text>Soon</Text>
										{/* <PriceChange percentage={marketCapChange} /> */}
									</>
								}
							/>
							<Box display={{ base: 'none', hashup: 'block' }}>
								<SpecificHeaderInfo
									text={t('gameOverview.cartridge')}
									body={
										<Cartridge
											padding="3px 5px"
											type={type.toUpperCase()}
											color="white"
										/>
									}
								/>
							</Box>
							<SpecificHeaderInfo
								variant={SpecificHeaderInfoVariant.SMALL}
								text={'Creator Fee'}
								body={<Text>{Number(ethers.utils.formatUnits(game.creatorFee || '0', 0))}%</Text>}
							/>
							<SpecificHeaderInfo
								variant={SpecificHeaderInfoVariant.SMALL}
								text={'Marketplace Fee'}
								body={<Text>{Number(ethers.utils.formatUnits(game.marketplaceFee || '0', 0))}%</Text>}
							/>
							{/* <PriceChange percentage={priceChange} isFilled /> */}
						</Flex>
						{/* TODO price in Hash */}
						{/* <Text fontSize="14px" lineHeight="17px" colors={`${textColor}80`}> */}
						{/*	{priceInHash} */}
						{/* </Text> */}
					</Flex>
				</Flex>
				{/* <Grid
				gridColumnGap={{ base: '0', md: '48px' }}
				alignItems="flex-start"
				gridArea="details"
				gridTemplateColumns="repeat(4, auto)"
				gridTemplateRows="auto auto"
				flexDirection="row"
				justifyContent={{ base: 'end', hashup: 'start' }}
			>
				<Box display={{ base: 'none', hashup: 'block' }}>
					<SpecificHeaderInfo
						text={t('gameOverview.totalSupply').toString()}
						body={<Text>{totalSupply}</Text>}
					/>
				</Box>
				<Flex display={{ base: 'flex', hashup: 'none' }} gridGap="18px" mr="6px"></Flex>
				<Box
					display={{ base: 'block', hashup: 'none' }}
					position="absolute"
					top="-32px"
					left="16px"
					transform="translateY(-100%)"
				>
					<Cartridge padding="3px 5px" type={type} colors="white" />
				</Box>
			</Grid> */}
			</Grid>
		</>
	);
};
